// ✅ src/pages/VehicleV2/LubeRequirementReport.jsx
// FULL UPDATED FILE
//
// ✅ common localStorage / sessionStorage pattern
// ✅ useMemo + useCallback + useRef pattern
// ✅ pageLoading + dropdownLoading + listLoading
// ✅ prevents repeated initial API calls
// ✅ safer search / view all / clear / print / export flow
// ✅ safer HO / BRANCH branch handling
// ✅ supplier multi-select support
//
// Backend mount:
//   app.use("/api/lubeRequirementReport", lubeRequirementReport);
//
// Recommended endpoints:
//   GET /branches
//   GET /months
//   GET /years
//   GET /itemCategory
//   GET /items
//   GET /suppliers
//   GET /list
//   GET /print

import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef,
  useCallback,
} from "react";
import axios from "axios";
import {
  FaSearch,
  FaPrint,
  FaFilter,
  FaArrowLeft,
  FaFileExport,
  FaInfoCircle,
  FaEye,
  FaSyncAlt,
} from "react-icons/fa";

import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";

const API_BASE = "https://nissi-india.com/api/lubeRequirementsReport";

/* ---------------- helpers ---------------- */
const toStr = (v, def = "") =>
  v === null || v === undefined ? def : String(v).trim();

const isSelect = (v) => {
  const s = toStr(v).toLowerCase();
  return !s || s === "select";
};

const toList = (arr) => (Array.isArray(arr) ? arr : []);

const exportToCsv = (rows, columns, filename = "lube-requirement-report.csv") => {
  if (!rows?.length) return;

  const headers = columns?.length ? columns : Object.keys(rows[0] || {});
  const csv = [
    headers.join(","),
    ...rows.map((r) =>
      headers
        .map((h) => {
          const val = r?.[h] ?? "";
          const s = String(val).replace(/"/g, '""');
          return `"${s}"`;
        })
        .join(",")
    ),
  ].join("\n");

  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.setAttribute("download", filename);
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

const createDefaultFilters = (branchType, branchId) => ({
  branchId: branchType === "HO" ? "Select" : String(branchId),
  monthName: "Select",
  year: "Select",
  itemCategoryId: "",
  itemId: "Select",
  supplierNames: [],
});

export default function LubeRequirementReport() {
  const navigate = useNavigate();
  const { isDark, skin, setSkin } = useContext(ThemeContext);

  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  /* ===========================
     shared local/session pattern
  =========================== */
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";

  const branchIdRaw =
    localStorage.getItem("branchId") ||
    localStorage.getItem("Branchid") ||
    localStorage.getItem("branchid") ||
    "8";

  const branchType =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "BRANCH";

  const userId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    localStorage.getItem("loginUserId") ||
    "0";

  const loginUserId = sessionStorage.getItem("loginUserId") || userId || "0";
  const sessionBranchId = Number(branchIdRaw || 8);

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": String(sessionBranchId),
      "x-branch": String(branchType),
      "x-userid": String(loginUserId),
    }),
    [finYearId, finYear, sessionBranchId, branchType, loginUserId]
  );

  const session = useMemo(
    () => ({
      branchId: sessionBranchId,
      branchType: toStr(branchType || "BRANCH"),
      loginUserId: Number(loginUserId || 0),
      userId: toStr(userId || "0"),
    }),
    [sessionBranchId, branchType, loginUserId, userId]
  );

  const defaultFilters = useMemo(
    () => createDefaultFilters(session.branchType, session.branchId),
    [session.branchType, session.branchId]
  );

  /* ===========================
     refs
  =========================== */
  const didInitRef = useRef(false);

  /* ===========================
     loading states
  =========================== */
  const [pageLoading, setPageLoading] = useState(false);
  const [dropdownLoading, setDropdownLoading] = useState(false);
  const [listLoading, setListLoading] = useState(false);

  const isBusy = pageLoading || dropdownLoading || listLoading;

  /* ===========================
     UI states
  =========================== */
  const [showFilters, setShowFilters] = useState(true);

  /* ===========================
     meta states
  =========================== */
  const [branches, setBranches] = useState([]);
  const [months, setMonths] = useState([]);
  const [years, setYears] = useState([]);
  const [itemCategory, setItemCategory] = useState(null);
  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);

  /* ===========================
     form states
  =========================== */
  const [filters, setFilters] = useState(defaultFilters);

  /* ===========================
     result states
  =========================== */
  const [rows, setRows] = useState([]);
  const [columns, setColumns] = useState([]);

  /* ===========================
     pagination
  =========================== */
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  /* ===========================
     styles
  =========================== */
  const inputClass = useMemo(
    () =>
      `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
        isDark
          ? "bg-gray-700/30 border-gray-600 text-white placeholder:text-gray-400"
          : "bg-white border-gray-300 text-gray-900 placeholder:text-gray-400"
      }`,
    [isDark]
  );

  const labelClass = useMemo(
    () =>
      `block text-sm font-medium mb-2 ${
        isDark ? "text-gray-300" : "text-gray-700"
      }`,
    [isDark]
  );

  const cardClass = useMemo(
    () =>
      `p-6 mt-6 rounded-2xl shadow-xl border ${
        isDark
          ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
          : "bg-white text-black"
      }`,
    [isDark]
  );

  /* ===========================
     derived helpers
  =========================== */
  const displayColumns = useMemo(() => {
    if (columns?.length) return columns;
    if (rows?.[0]) return Object.keys(rows[0]);
    return [];
  }, [columns, rows]);

  const totalPages = Math.max(1, Math.ceil(rows.length / itemsPerPage));

  const pagedRows = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return rows.slice(start, start + itemsPerPage);
  }, [rows, currentPage]);

  /* ===========================
     api loaders
  =========================== */
  const loadItems = useCallback(
    async (itemCategoryId) => {
      try {
        if (!itemCategoryId || isSelect(itemCategoryId)) {
          setItems([]);
          return;
        }

        const { data } = await axios.get(`${API_BASE}/items`, {
          params: { itemCategoryId },
          headers: commonHeaders,
        });

        setItems(toList(data?.rows));
      } catch (err) {
        console.error(err);
        setItems([]);
        toast.error(
          err?.response?.data?.message || err?.message || "Failed to load items"
        );
      }
    },
    [commonHeaders]
  );

  const loadSuppliers = useCallback(
    async (branchIdValue) => {
      try {
        if (!branchIdValue || isSelect(branchIdValue)) {
          setSuppliers([]);
          return;
        }

        const { data } = await axios.get(`${API_BASE}/suppliers`, {
          params: { branchId: branchIdValue },
          headers: commonHeaders,
        });

        setSuppliers(toList(data?.rows));
      } catch (err) {
        console.error(err);
        setSuppliers([]);
        toast.error(
          err?.response?.data?.message || err?.message || "Failed to load suppliers"
        );
      }
    },
    [commonHeaders]
  );

  const loadMeta = useCallback(async () => {
    try {
      setDropdownLoading(true);

      const [brRes, moRes, yrRes, catRes] = await Promise.all([
        axios.get(`${API_BASE}/branches`, {
          params: {
            branchType: session.branchType,
            branchId: session.branchId,
          },
          headers: commonHeaders,
        }),
        axios.get(`${API_BASE}/months`, { headers: commonHeaders }),
        axios.get(`${API_BASE}/years`, { headers: commonHeaders }),
        axios.get(`${API_BASE}/itemCategory`, { headers: commonHeaders }),
      ]);

      const br = toList(brRes?.data?.rows);
      const mo = toList(moRes?.data?.rows);
      const yr = toList(yrRes?.data?.rows);
      const cat = catRes?.data?.row || null;

      setBranches(br);
      setMonths(mo);
      setYears(yr);
      setItemCategory(cat);

      const nextItemCategoryId = String(cat?.itemCategoryId || "");

      setFilters((prev) => ({
        ...prev,
        branchId:
          session.branchType === "HO"
            ? prev.branchId
            : String(session.branchId),
        itemCategoryId: nextItemCategoryId,
      }));

      if (nextItemCategoryId) {
        await loadItems(nextItemCategoryId);
      }

      if (session.branchType !== "HO") {
        await loadSuppliers(String(session.branchId));
      }
    } catch (err) {
      console.error(err);
      toast.error(
        err?.response?.data?.message || err?.message || "Failed to load meta"
      );
      setBranches([]);
      setMonths([]);
      setYears([]);
      setItemCategory(null);
      setItems([]);
      setSuppliers([]);
    } finally {
      setDropdownLoading(false);
    }
  }, [
    session.branchType,
    session.branchId,
    commonHeaders,
    loadItems,
    loadSuppliers,
  ]);

  const buildListParams = useCallback(
    (f = filters, override = {}) => ({
      branchId:
        session.branchType === "HO"
          ? (isSelect(f.branchId) ? "Select" : toStr(f.branchId))
          : String(session.branchId),
      monthName: isSelect(f.monthName) ? "Select" : toStr(f.monthName),
      year: isSelect(f.year) ? "Select" : toStr(f.year),
      itemCategoryId: toStr(f.itemCategoryId),
      itemId: isSelect(f.itemId) ? "" : toStr(f.itemId),
      suppliers: Array.isArray(f.supplierNames) ? f.supplierNames : [],
      date: "",
      ...override,
    }),
    [filters, session.branchType, session.branchId]
  );

  const loadList = useCallback(
    async (nextFilters = filters, silent = false) => {
      try {
        setListLoading(true);

        const { data } = await axios.get(`${API_BASE}/list`, {
          params: buildListParams(nextFilters),
          headers: commonHeaders,
        });

        if (!data?.success) {
          throw new Error(data?.message || "Search failed");
        }

        const list = toList(data?.rows);
        const cols = list[0] ? Object.keys(list[0]) : [];

        setRows(list);
        setColumns(cols);
        setCurrentPage(1);

        if (!silent) toast.success(`Found ${list.length} record(s)`);
      } catch (err) {
        console.error(err);
        toast.error(
          err?.response?.data?.message || err?.message || "Search failed"
        );
        setRows([]);
        setColumns([]);
        setCurrentPage(1);
      } finally {
        setListLoading(false);
      }
    },
    [filters, buildListParams, commonHeaders]
  );

  const loadPage = useCallback(async () => {
    try {
      setPageLoading(true);

      const baseFilters = createDefaultFilters(session.branchType, session.branchId);

      setFilters(baseFilters);
      setRows([]);
      setColumns([]);
      setSuppliers([]);
      setCurrentPage(1);

      await loadMeta();
    } finally {
      setPageLoading(false);
    }
  }, [session.branchType, session.branchId, loadMeta]);

  /* ===========================
     handlers
  =========================== */
  const handleChange = useCallback(
    async (e) => {
      const { name, value } = e.target;

      setFilters((prev) => ({
        ...prev,
        [name]: value,
      }));

      if (name === "branchId" && session.branchType === "HO") {
        setFilters((prev) => ({
          ...prev,
          branchId: value,
          supplierNames: [],
        }));
        await loadSuppliers(value);
      }

      if (name === "itemCategoryId") {
        setFilters((prev) => ({
          ...prev,
          itemCategoryId: value,
          itemId: "Select",
        }));
        await loadItems(value);
      }
    },
    [session.branchType, loadSuppliers, loadItems]
  );

  const toggleSupplier = useCallback((supplierName) => {
    setFilters((prev) => {
      const exists = prev.supplierNames.includes(supplierName);
      const next = exists
        ? prev.supplierNames.filter((x) => x !== supplierName)
        : [...prev.supplierNames, supplierName];

      return {
        ...prev,
        supplierNames: next,
      };
    });
  }, []);

  const validate = useCallback((f = filters) => {
    if (isSelect(f.branchId)) return "Please select Branch";
    if (isSelect(f.monthName)) return "Please select Month";
    if (isSelect(f.year)) return "Please select Year";
    if (isSelect(f.itemCategoryId)) return "Item Category missing";
    return "";
  }, [filters]);

  const handleSearch = useCallback(async () => {
    const msg = validate(filters);
    if (msg) {
      toast.warning(msg);
      return;
    }
    await loadList(filters);
  }, [filters, validate, loadList]);

  const handleViewAll = useCallback(async () => {
    const nextFilters = {
      ...filters,
      itemId: "Select",
      supplierNames: [],
    };

    const msg = validate(nextFilters);
    if (msg) {
      toast.warning(msg);
      return;
    }

    setFilters(nextFilters);
    await loadList(nextFilters, true);
    toast.info("Showing all records for selected filters");
  }, [filters, validate, loadList]);

  const handleClearFilters = useCallback(async () => {
    const cleared = {
      ...createDefaultFilters(session.branchType, session.branchId),
      itemCategoryId: toStr(itemCategory?.itemCategoryId || ""),
    };

    setFilters(cleared);
    setRows([]);
    setColumns([]);
    setCurrentPage(1);

    if (session.branchType === "HO") {
      setSuppliers([]);
    } else {
      await loadSuppliers(String(session.branchId));
    }

    toast.info("Cleared filters");
  }, [session.branchType, session.branchId, itemCategory, loadSuppliers]);

  const handleRefresh = useCallback(async () => {
    await loadPage();
    toast.info("Page refreshed");
  }, [loadPage]);

  const handleExport = useCallback(() => {
    if (!rows.length) {
      toast.warning("No data to export");
      return;
    }
    exportToCsv(rows, displayColumns, "lube-requirement-report.csv");
    toast.success("Exported CSV");
  }, [rows, displayColumns]);

  const handlePrint = useCallback(() => {
    const msg = validate(filters);
    if (msg) {
      toast.warning(msg);
      return;
    }

    const suppliersCsv =
      Array.isArray(filters.supplierNames) && filters.supplierNames.length
        ? filters.supplierNames.join(",") + ","
        : "";

    navigate("/vehicle/LubeRequirementsReport_Print", {
      state: {
        filters: {
          branchId:
            session.branchType === "HO"
              ? (isSelect(filters.branchId) ? "" : toStr(filters.branchId))
              : String(session.branchId),
          monthName: toStr(filters.monthName),
          year: toStr(filters.year),
          itemCategoryId: toStr(filters.itemCategoryId),
          itemId: isSelect(filters.itemId) ? "" : toStr(filters.itemId),
          suppliers: suppliersCsv,
          date: "",
        },
      },
    });
  }, [filters, validate, navigate, session.branchType, session.branchId]);

  /* ===========================
     effects
  =========================== */
  useEffect(() => {
    if (didInitRef.current) return;
    didInitRef.current = true;
    loadPage();
  }, [loadPage]);

  /* ===========================
     pagination
  =========================== */
  const paginate = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">LUBE REQUIREMENT REPORT</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div className={cardClass}>
          {/* Top header */}
          <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4 mb-6">
            <div className="flex flex-wrap items-center gap-4">
              <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                Lube Requirement Report
              </h1>

              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isDark ? "bg-blue-900 text-blue-100" : "bg-blue-100 text-blue-800"
                }`}
              >
                {rows.length} Records
              </span>

              <span
                className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isBusy
                    ? isDark
                      ? "bg-yellow-900 text-yellow-100"
                      : "bg-yellow-100 text-yellow-800"
                    : isDark
                    ? "bg-green-900 text-green-100"
                    : "bg-green-100 text-green-800"
                }`}
              >
                {pageLoading
                  ? "Loading Page"
                  : dropdownLoading
                  ? "Loading Dropdowns"
                  : listLoading
                  ? "Loading List"
                  : "Ready"}
              </span>
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <button
                onClick={() => setShowFilters((s) => !s)}
                disabled={isBusy}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? `border-gray-600 text-white hover:bg-gray-800 ${
                        showFilters ? "bg-gray-800" : ""
                      }`
                    : `border-gray-300 text-gray-700 hover:bg-gray-50 ${
                        showFilters ? "bg-gray-50" : ""
                      }`
                } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaFilter className="w-4 h-4" />
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>

              <button
                onClick={handleExport}
                disabled={isBusy || rows.length === 0}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                } ${isBusy || rows.length === 0 ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaFileExport className="w-4 h-4" />
                Export
              </button>

              <button
                onClick={handleRefresh}
                disabled={isBusy}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaSyncAlt className="w-4 h-4" />
                Refresh
              </button>

              <button
                onClick={() => navigate(-1)}
                disabled={isBusy}
                type="button"
                className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                  isDark
                    ? "border-gray-600 text-white hover:bg-gray-800"
                    : "border-gray-300 text-gray-700 hover:bg-gray-50"
                } ${isBusy ? "opacity-60 cursor-not-allowed" : ""}`}
              >
                <FaArrowLeft className="w-4 h-4" />
                Back
              </button>
            </div>
          </div>

          {pageLoading || dropdownLoading ? (
            <div className="text-center py-16">
              <div
                className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 ${
                  isDark ? "bg-gray-700" : "bg-gray-100"
                }`}
              >
                <FaInfoCircle className="w-10 h-10 text-gray-400" />
              </div>
              <h3 className={`text-xl font-semibold mb-2 ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                Loading...
              </h3>
              <p className={`mb-6 max-w-sm mx-auto ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                Please wait while data is loading.
              </p>
            </div>
          ) : (
            <>
              {/* Filters */}
              {showFilters && (
                <div
                  className={`rounded-xl p-6 mb-6 border ${
                    isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
                  }`}
                >
                  <h3
                    className={`text-lg font-semibold flex items-center gap-2 mb-4 ${
                      isDark ? "text-white" : "text-gray-900"
                    }`}
                  >
                    <FaFilter /> FILTRATION AREA
                  </h3>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
                    <div>
                      <label className={labelClass}>Branch</label>
                      <select
                        name="branchId"
                        value={filters.branchId}
                        onChange={handleChange}
                        className={inputClass}
                        disabled={isBusy || session.branchType !== "HO"}
                      >
                        <option value="Select">Select</option>
                        {branches.map((b, idx) => (
                          <option
                            key={idx}
                            value={String(b.branchId ?? b.fld_branchid ?? b.id ?? "")}
                          >
                            {b.branchName ?? b.fld_BranchName ?? b.name ?? ""}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className={labelClass}>Month</label>
                      <select
                        name="monthName"
                        value={filters.monthName}
                        onChange={handleChange}
                        className={inputClass}
                        disabled={isBusy}
                      >
                        <option value="Select">Select</option>
                        {months.map((m) => (
                          <option key={m.value} value={m.name}>
                            {m.name}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className={labelClass}>Year</label>
                      <select
                        name="year"
                        value={filters.year}
                        onChange={handleChange}
                        className={inputClass}
                        disabled={isBusy}
                      >
                        <option value="Select">Select</option>
                        {years.map((y) => (
                          <option key={y.year} value={y.year}>
                            {y.year}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className={labelClass}>Item Category</label>
                      <input
                        className={inputClass}
                        value={itemCategory?.itemCategoryName || "FUELS AND LUBRICANTS"}
                        readOnly
                      />
                    </div>

                    <div>
                      <label className={labelClass}>Item Name</label>
                      <select
                        name="itemId"
                        value={filters.itemId}
                        onChange={handleChange}
                        className={inputClass}
                        disabled={isBusy}
                      >
                        <option value="Select">Select</option>
                        {items.map((it) => (
                          <option
                            key={it.itemId ?? it.fld_itemid}
                            value={String(it.itemId ?? it.fld_itemid)}
                          >
                            {it.itemName ?? it.fld_itemname}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="md:col-span-2 lg:col-span-3">
                      <label className={labelClass}>Supplier (multi-select)</label>
                      <div
                        className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 p-3 rounded-lg border ${
                          isDark ? "border-gray-600 bg-gray-700/30" : "border-gray-200 bg-white"
                        }`}
                      >
                        {suppliers.length === 0 ? (
                          <div className={`text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                            No suppliers found for selected branch
                          </div>
                        ) : (
                          suppliers.map((s) => {
                            const supplierName = s.supplierName ?? s.fld_suppliername ?? "";
                            const supplierId = s.supplierId ?? s.fld_supplierid ?? supplierName;
                            const checked = filters.supplierNames.includes(supplierName);

                            return (
                              <label
                                key={supplierId}
                                className={`flex items-center gap-2 text-sm cursor-pointer ${
                                  isDark ? "text-gray-200" : "text-gray-800"
                                }`}
                              >
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={() => toggleSupplier(supplierName)}
                                  disabled={isBusy}
                                />
                                {supplierName}
                              </label>
                            );
                          })
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mt-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <button
                      onClick={handleClearFilters}
                      type="button"
                      disabled={isBusy}
                      className={`text-gray-600 dark:text-gray-300 hover:text-gray-800 dark:hover:text-white font-medium transition-colors duration-200 ${
                        isBusy ? "opacity-60 cursor-not-allowed" : ""
                      }`}
                    >
                      Clear All Filters
                    </button>

                    <div className="flex flex-wrap gap-4">
                      <button
                        onClick={handleViewAll}
                        disabled={isBusy}
                        type="button"
                        className={`flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                          isBusy ? "opacity-60 cursor-not-allowed" : ""
                        }`}
                      >
                        <FaEye />
                        View All
                      </button>

                      <button
                        onClick={handleSearch}
                        disabled={isBusy}
                        type="button"
                        className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                          isBusy ? "opacity-60 cursor-not-allowed" : ""
                        }`}
                      >
                        <FaSearch />
                        {listLoading ? "Loading..." : "Search Records"}
                      </button>

                      <button
                        onClick={handlePrint}
                        disabled={isBusy}
                        type="button"
                        className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                          isBusy ? "opacity-60 cursor-not-allowed" : ""
                        }`}
                      >
                        <FaPrint />
                        Print
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Results */}
              <div
                className={`rounded-xl shadow-sm border overflow-hidden ${
                  isDark ? "border-gray-700" : "border-gray-200"
                }`}
              >
                {rows.length === 0 ? (
                  <div className="text-center py-16">
                    <div
                      className={`inline-flex items-center justify-center w-20 h-20 rounded-full mb-6 ${
                        isDark ? "bg-gray-700" : "bg-gray-100"
                      }`}
                    >
                      <FaInfoCircle className="w-10 h-10 text-gray-400" />
                    </div>
                    <h3 className={`text-xl font-semibold mb-2 ${isDark ? "text-gray-200" : "text-gray-800"}`}>
                      No Records
                    </h3>
                    <p className={`mb-6 max-w-sm mx-auto ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                      Select Branch, Month and Year, then click Search.
                    </p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead className={`${buttonClass} text-white`}>
                        <tr>
                          {displayColumns.map((c) => (
                            <th
                              key={c}
                              className="px-4 py-3 text-center font-semibold whitespace-nowrap"
                            >
                              {c}
                            </th>
                          ))}
                        </tr>
                      </thead>

                      <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                        {pagedRows.map((r, idx) => (
                          <tr
                            key={idx}
                            className={`${
                              isDark ? "bg-gray-800 hover:bg-gray-700" : "bg-white hover:bg-gray-50"
                            } transition-colors duration-200`}
                          >
                            {displayColumns.map((c) => (
                              <td
                                key={c}
                                className={`px-4 py-3 text-sm text-center whitespace-nowrap ${
                                  isDark ? "text-gray-300" : "text-gray-900"
                                }`}
                              >
                                {String(r?.[c] ?? "")}
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Pagination */}
              {rows.length > 0 && (
                <div className="mt-6 flex flex-col sm:flex-row justify-between items-center">
                  <div className={`text-sm mb-4 sm:mb-0 ${isDark ? "text-gray-400" : "text-gray-600"}`}>
                    Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
                    {Math.min(currentPage * itemsPerPage, rows.length)} of {rows.length} entries
                  </div>

                  <nav className="flex gap-1">
                    <button
                      onClick={() => paginate(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                      className={`px-3 py-1 border rounded text-sm ${
                        currentPage === 1
                          ? isDark
                            ? "border-gray-700 text-gray-500 cursor-not-allowed"
                            : "border-gray-300 text-gray-400 cursor-not-allowed"
                          : isDark
                          ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                          : "border-gray-300 text-gray-700 hover:bg-gray-100"
                      }`}
                    >
                      Previous
                    </button>

                    {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                      let pageNum;
                      if (totalPages <= 5) pageNum = i + 1;
                      else if (currentPage <= 3) pageNum = i + 1;
                      else if (currentPage >= totalPages - 2) pageNum = totalPages - 4 + i;
                      else pageNum = currentPage - 2 + i;

                      return (
                        <button
                          key={pageNum}
                          onClick={() => paginate(pageNum)}
                          className={`px-3 py-1 border rounded text-sm ${
                            currentPage === pageNum
                              ? `${buttonClass} text-white`
                              : isDark
                              ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                              : "border-gray-300 text-gray-700 hover:bg-gray-100"
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}

                    <button
                      onClick={() => paginate(Math.min(totalPages, currentPage + 1))}
                      disabled={currentPage === totalPages}
                      className={`px-3 py-1 border rounded text-sm ${
                        currentPage === totalPages
                          ? isDark
                            ? "border-gray-700 text-gray-500 cursor-not-allowed"
                            : "border-gray-300 text-gray-400 cursor-not-allowed"
                          : isDark
                          ? "border-gray-600 text-gray-300 hover:bg-gray-700"
                          : "border-gray-300 text-gray-700 hover:bg-gray-100"
                      }`}
                    >
                      Next
                    </button>
                  </nav>
                </div>
              )}

              {isBusy && (
                <div className={`mt-4 text-sm ${isDark ? "text-gray-300" : "text-gray-600"}`}>
                  {pageLoading
                    ? "Loading page..."
                    : dropdownLoading
                    ? "Loading dropdowns..."
                    : listLoading
                    ? "Loading list..."
                    : "Loading..."}
                </div>
              )}
            </>
          )}

          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
}